import React, { useEffect, useRef, useState } from "react";
import useStore from "../../../../useStore";
import Loader from "../../../Elements/Loader/Loader";
import Button from "../../../Elements/Button/Button";
import { buildMapsUrl } from "../../../../Utilities/Maps/BuildUri";
import useGeolocation from "../../../../Hooks/useGeolocation";
import { annotateWithDistance, filterByRadius, isValidCoord, RADIUS_KM_ADMINISTRADORES } from "../../../../Utilities/geocode";

const RestauranteIsActive = () => {
    const {
        inactiveRestaurants,
        loadingInactiveRestaurants,
        errorInactiveRestaurants,
        fetchDataInactiveRestaurants,
        activateRestaurant
    } = useStore();

    const [pendingIds, setPendingIds] = useState(new Set());
    const { status: geoStatus, coords } = useGeolocation();

    const didFetchRef = useRef(false);
    useEffect(() => {
        if (didFetchRef.current) return;
        didFetchRef.current = true;
        fetchDataInactiveRestaurants();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loadingInactiveRestaurants) {
        return (
            <div className="container my-4 text-center">
                <Loader />
            </div>
        );
    }

    const onActivateClick = async (restauranteId) => {
        if (!restauranteId || pendingIds.has(restauranteId)) return;
        setPendingIds((prev) => new Set(prev).add(restauranteId));
        try {
            const res = await activateRestaurant(restauranteId);
            // Opcional: feedback
            /* console.log("✅ Activado:", res); */
            // Nota: el store ya elimina al activado de la lista.
        } catch (e) {
            console.error("❌ Error activando:", e);
            alert(e?.message || "No se pudo activar");
        } finally {
            setPendingIds((prev) => {
                const next = new Set(prev);
                next.delete(restauranteId);
                return next;
            });
        }
    };

    if (errorInactiveRestaurants) {
        return (
            <div className="container my-4">
                <div className="alert alert-danger d-flex justify-content-between align-items-center">
                    <span>⚠️ {errorInactiveRestaurants}</span>
                    <Button
                        text="Reintentar"
                        color="light"
                        fill
                        action={fetchDataInactiveRestaurants}
                    />
                </div>
            </div>
        );
    }

    const baseItems = inactiveRestaurants || [];

    let items = baseItems;

    // Si el administrador concedió ubicación, filtra por radio de 10 km (RADIUS_KM_ADMINISTRADORES)
    if (geoStatus === "granted" && coords && isValidCoord(coords.lat, coords.lng)) {
        const candidates = baseItems.filter((r) => isValidCoord(r.lat, r.lng));
        const withDist = annotateWithDistance({ lat: coords.lat, lng: coords.lng }, candidates);
        items = filterByRadius(withDist, RADIUS_KM_ADMINISTRADORES);
    }

    return (
        <div className="container-fluid container-mh my-4">
            <div className="row mb-3 align-items-center">
                <div className="col">
                    <h3 className="mb-0">Comercios inactivos</h3>
                </div>
                <div className="col-auto">
                    <Button
                        text="Refrescar"
                        color="blue"
                        fill
                        classNames="btn-sm"
                        action={fetchDataInactiveRestaurants}
                    />
                </div>
            </div>

            {items.length === 0 ? (
                <div className="alert alert-success">No hay comercios inactivos 🎉</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-striped table-bordered align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Nombre</th>
                                <th>Email / Teléfono</th>
                                <th>Categorías</th>
                                <th>Dirección</th>
                                <th>Entrega (min)</th>
                                <th>Envío</th>
                                <th>Productos</th>
                                <th>Maps</th>
                                <th>Activar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => {
                                const cats = Array.isArray(r.categorias)
                                    ? r.categorias.join(", ")
                                    : "";
                                const productsCount = Array.isArray(r.productos)
                                    ? r.productos.length
                                    : 0;
                                const mapsUrl = buildMapsUrl({
                                    lat: r.lat,
                                    lng: r.lng,
                                    direccion: r.direccion,
                                    name: r.name,
                                });

                                const isPending = pendingIds.has(r.restauranteId);

                                return (
                                    <tr key={r.restauranteId}>
                                        <td>{r.name}</td>
                                        <td>
                                            <div>{r.email}</div>
                                            <small className="text-muted">{r.phone}</small>
                                        </td>
                                        <td>{cats}</td>
                                        <td style={{ maxWidth: "250px" }}>
                                            <div className="text-truncate" title={r.direccion}>
                                                {r.direccion}
                                            </div>
                                        </td>
                                        <td>{r.tiempoEntregaEstimado ?? "-"}</td>
                                        <td>
                                            {typeof r.costoEnvio === "number"
                                                ? `$${r.costoEnvio}`
                                                : "-"}
                                        </td>
                                        <td>{productsCount}</td>
                                        <td>
                                            <Button
                                                text="Abrir en Maps"
                                                color="blue"
                                                fill
                                                disabled={false}
                                                action={() => window.open(mapsUrl, "_blank", "noopener,noreferrer")}
                                            />
                                        </td>
                                        <td>
                                            <Button
                                                text={isPending ? "Activando..." : "Activar"}
                                                color="green"
                                                fill
                                                disabled={isPending}
                                                classNames="btn-sm"
                                                action={() => onActivateClick(r.restauranteId)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default RestauranteIsActive;